import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Icon } from "@iconify/react";
import {
  NavLink as RouterLink,
  matchPath,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useSnackbar } from "notistack";
import arrowIosForwardFill from "@iconify/icons-eva/arrow-ios-forward-fill";
import arrowIosDownwardFill from "@iconify/icons-eva/arrow-ios-downward-fill";
import lockFill from "@iconify/icons-eva/lock-fill";
import settings2Fill from "@iconify/icons-eva/settings-2-fill";
import pieChart2Fill from "@iconify/icons-eva/pie-chart-2-fill";
import peopleFill from "@iconify/icons-eva/people-fill";
import personFill from "@iconify/icons-eva/person-fill";
import logOutFill from "@iconify/icons-eva/log-out-fill";
import { alpha, useTheme, styled } from "@mui/material/styles";
import {
  Box,
  List,
  Collapse,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  Drawer,
  Typography,
} from "@mui/material";

import Logo from "../components/Logo";
import Scrollbar from "../components/Scrollbar";
import { MHidden } from "../components/@material-extend";
import ConfirmDialog from "../components/common/ConfirmDialog";

import { logout } from "../DAL/AUTH";

// ----------------------------------------------------------------------

const DRAWER_WIDTH = 280;

const getIcon = (name) => <Icon icon={name} width={22} height={22} />;

const sidebarConfig = [
  {
    title: "Dashboard",
    path: "/dashboard",
    icon: getIcon(pieChart2Fill),
  },
  {
    title: "Coaches",
    path: "/coaches",
    icon: getIcon(peopleFill),
  },
  {
    title: "Settings",
    path: "/settings",
    icon: getIcon(settings2Fill),
    children: [
      {
        title: "Profile",
        path: "/profile",
        icon: getIcon(personFill),
      },
      {
        title: "Two Factor Auth",
        path: "/two-factor-authentication",
        icon: getIcon("eva:shield-outline"),
        
      },
      {
        title: "Change Password",
        path: "/change-password",
        icon: getIcon(lockFill),
      },
    ],
  },
];

const RootStyle = styled("div")(({ theme }) => ({
  [theme.breakpoints.up("lg")]: {
    flexShrink: 0,
    width: DRAWER_WIDTH,
  },
}));

const ListItemStyle = styled((props) => (
  <ListItemButton disableGutters {...props} />
))(({ theme }) => ({
  ...theme.typography.body2,
  height: 48,
  position: "relative",
  textTransform: "capitalize",
  paddingLeft: theme.spacing(3),
  paddingRight: theme.spacing(2.5),
  color: theme.palette.text.secondary,
  borderRadius: theme.shape.borderRadius,
  marginBottom: theme.spacing(0.5),
  marginRight: theme.spacing(1.5),
  marginLeft: theme.spacing(1.5),
  "&:before": {
    top: 0,
    right: 0,
    width: 3,
    bottom: 0,
    content: "''",
    display: "none",
    position: "absolute",
    borderBottomLeftRadius: 4,
    backgroundColor: theme.palette.primary.main,
  },
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
    color: theme.palette.primary.main,
  },
}));

const ListItemIconStyle = styled(ListItemIcon)({
  width: 22,
  height: 22,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "inherit",
});

function NavItem({ item, active }) {
  const theme = useTheme();
  const isActiveRoot = active(item.path);
  const { title, path, icon, info, children } = item;
  const [open, setOpen] = useState(isActiveRoot);

  const handleOpen = () => {
    setOpen((prev) => !prev);
  };

  const activeRootStyle = {
    color: "primary.main",
    fontWeight: "fontWeightMedium",
    bgcolor: alpha(theme.palette.primary.main, 0.08),
    "&:before": { display: "block" },
  };

  const activeSubStyle = {
    color: "primary.main",
    fontWeight: "fontWeightMedium",
    "&:before": { display: "block" },
  };

  useEffect(() => {
    setOpen(isActiveRoot);
  }, [isActiveRoot]);

  if (children) {
    return (
      <>
        <ListItemStyle
          onClick={handleOpen}
          sx={{
            ...(isActiveRoot && activeRootStyle),
          }}
        >
          <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
          <ListItemText disableTypography primary={title} />
          {info && info}
          <Box
            component={Icon}
            icon={open ? arrowIosDownwardFill : arrowIosForwardFill}
            sx={{ width: 16, height: 16, ml: 1 }}
          />
        </ListItemStyle>

        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {children.map((item) => {
              const { title, path, icon } = item;
              const isActiveSub = active(path);
              return (
                <ListItemStyle
                  key={title}
                  component={RouterLink}
                  to={path}
                  sx={{
                    ...(isActiveSub && activeSubStyle),
                    pl: 4,
                  }}
                >
                  <ListItemIconStyle>{icon}</ListItemIconStyle>
                  <ListItemText disableTypography primary={title} />
                </ListItemStyle>
              );
            })}
          </List>
        </Collapse>
      </>
    );
  }

  return (
    <ListItemStyle
      component={RouterLink}
      to={path}
      sx={{
        ...(isActiveRoot && activeRootStyle),
      }}
    >
      <ListItemIconStyle>{icon && icon}</ListItemIconStyle>
      <ListItemText disableTypography primary={title} />
      {info && info}
    </ListItemStyle>
  );
}

NavItem.propTypes = {
  item: PropTypes.object,
  active: PropTypes.func,
};

function NavSection({ navConfig, ...other }) {
  const { pathname } = useLocation();

  const matchSideBar = (path) =>
    path ? !!matchPath({ path, end: false }, pathname) : false;

  return (
    <Box {...other}>
      <List disablePadding sx={{ px: 0 }}>
        {navConfig.map((item) => (
          <NavItem key={item.title} item={item} active={matchSideBar} />
        ))}
      </List>
    </Box>
  );
}

NavSection.propTypes = {
  navConfig: PropTypes.array,
};

export default function Sidebar({ isOpenSidebar, onCloseSidebar }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [openLogout, setOpenLogout] = useState(false);

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
  }, [pathname]);

  const handleLogout = async () => {
    const response = await logout();
    if (response.code === 200) {
      enqueueSnackbar(response.message, { variant: "success" });
      localStorage.clear();
      navigate("/login");
    } else {
      enqueueSnackbar(response.message, { variant: "error" });
    }
  };

  const renderContent = (
    <Scrollbar
      sx={{
        height: "100%",
        "& .simplebar-content": {
          height: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box sx={{ px: 2.5, py: 3 }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          <Logo sx={{ width: 40 }} />
          <Box sx={{ ml: 2 }}>
            <Typography
              variant="subtitle1"
              sx={{ color: "text.primary", fontWeight: "bold" }}
            >
              Core Ni
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Super Admin
            </Typography>
          </Box>
        </Box>
      </Box>

      <NavSection navConfig={sidebarConfig} />

      <Box sx={{ flexGrow: 1 }} />

      <Box sx={{ pb: 3, mt: 10 }}>
        <ListItemStyle
          onClick={() => setOpenLogout(true)}
          sx={{
            color: "error.main",
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
              color: "error.main",
            },
          }}
        >
          <ListItemIconStyle>
            <Icon icon={logOutFill} width={22} height={22} />
          </ListItemIconStyle>
          <ListItemText disableTypography primary="Logout" />
        </ListItemStyle>
      </Box>
    </Scrollbar>
  );

  return (
    <RootStyle>
      <MHidden width="lgUp">
        <Drawer
          open={isOpenSidebar}
          onClose={onCloseSidebar}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: "background.paper",
              borderRight: "1px solid",
              borderColor: "divider",
            },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          open
          variant="persistent"
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              bgcolor: "background.paper",
              borderRight: "1px solid",
              borderColor: "divider",
            },
          }}
        >
          {renderContent}
        </Drawer>
      </MHidden>

      <ConfirmDialog
        open={openLogout}
        onClose={() => setOpenLogout(false)}
        onConfirm={handleLogout}
        title="Logout"
        message="Are you sure you want to logout?"
      />
    </RootStyle>
  );
}

Sidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func,
};
